import React, { useState, useEffect, useCallback } from 'react';
import { useParams, useSearchParams } from 'react-router-dom';
import {
    BookOpen, AlertCircle, CheckCircle, Target, Zap, Calendar, CreditCard, CalendarClock, Info, XCircle, UserX
} from 'lucide-react';
import api from '../../api';
import DashboardLayout from '../../layouts/DashboardLayout';
import { Card } from '../../components/ui/CCard';
import DueFollowUpsCard from '../../components/dashboard/DueFollowUpsCard';
import RecentActivityCard from '../../components/dashboard/RecentActivityCard';
import { usePeriodFilter, PeriodPicker, ALL_TIME_OPTION, PERIOD_OPTIONS } from '../../components/dashboard/financePeriod';
import { Tooltip } from '../../components/ui/Tooltip';

const InfoTip = ({ text }) => (
    <Tooltip content={<span className="block max-w-[220px]">{text}</span>} position="top">
        <Info className="w-3.5 h-3.5 text-secondary-400 hover:text-secondary-600 cursor-help" />
    </Tooltip>
);

const SESSION_PERIOD_OPTIONS = [ALL_TIME_OPTION, ...PERIOD_OPTIONS];



const TherapistDashboard = () => {
    const { id } = useParams(); // Session ID if viewing specific session details
    const [searchParams] = useSearchParams();
    // Supervisor passes ?doctor_id=X to view a reportee's analytics
    const viewingDoctorId = searchParams.get('doctor_id') || null;

    const [loading, setLoading] = useState(true);
    const [sessionData, setSessionData] = useState(null);
    const [benchmarkData, setBenchmarkData] = useState([]);
    const [heatmapData, setHeatmapData] = useState(null);
    const [progressData, setProgressData] = useState(null);
    const [activeTab, setActiveTab] = useState('overview');

    // Session data shown directly on Home, no tabs - Finance moved to its
    // own nav section (see src/pages/finance/FinanceHome.js) and Clinical
    // Skills (competency/behavioral charts) is hidden for now.
    const [sessionCount, setSessionCount] = useState(0);
    const [personalCredits, setPersonalCredits] = useState(0);
    const [recentSessions, setRecentSessions] = useState([]);
    const [dayCounts, setDayCounts] = useState({
        yesterday: { total: 0, missed: 0, cancelled: 0 },
        today: { total: 0, missed: 0, cancelled: 0 },
        tomorrow: { total: 0 },
    });
    const { period: sessionPeriod, setPeriod: setSessionPeriod, customStart: sessionCustomStart,
        setCustomStart: setSessionCustomStart, customEnd: sessionCustomEnd, setCustomEnd: setSessionCustomEnd,
        range: sessionRange } = usePeriodFilter({ period: 'all_time' });

    const fetchSessionStats = useCallback(async (startDate = null, endDate = null) => {
        try {
            const [sessionRes, profileRes, consultationsRes] = await Promise.all([
                api.getSessionCount(viewingDoctorId, startDate, endDate),
                api.getProfileDetails(),
                api.getAllConsultations(viewingDoctorId ? { doctor_id: viewingDoctorId } : {}),
            ]);
            setSessionCount(sessionRes.data.session_count || 0);
            setPersonalCredits(profileRes.data?.personal_credit ?? 0);
            const sortedSessions = (consultationsRes.data?.data || [])
                .sort((a, b) => new Date(b.created_at) - new Date(a.created_at))
                .slice(0, 5);
            setRecentSessions(sortedSessions);
        } catch (err) {
            console.error('Failed to load session stats', err);
        }
    }, [viewingDoctorId]);

    useEffect(() => {
        if (sessionPeriod === 'custom' && (!sessionRange || sessionRange.end < sessionRange.start)) return;
        if (id) return; // benchmark view doesn't need the session widgets
        fetchSessionStats(sessionRange?.start || null, sessionRange?.end || null);
    }, [id, sessionPeriod, sessionCustomStart, sessionCustomEnd, fetchSessionStats]);

    // Yesterday/Today/Tomorrow appointment counts - not period-filtered (the
    // dropdown above is for sessions), scoped server-side to exactly what
    // this user is allowed to see (own appointments for a doctor, the
    // viewed reportee's for a supervisor - see
    // get_appointment_day_counts()).
    useEffect(() => {
        if (id) return;
        api.getAppointmentDayCounts(viewingDoctorId)
            .then((res) => res.data && setDayCounts(res.data))
            .catch((err) => console.error('Failed to load appointment day counts', err));
    }, [id, viewingDoctorId]);

    useEffect(() => {
        // If ID is present, we are viewing a specific session's benchmark
        if (id) {
            fetchSessionBenchmark(id);
        } else {
            setLoading(false);
        }
    }, [id]);

    const fetchSessionBenchmark = async (sessionId) => {
        try {
            setLoading(true);
            const [sessionRes, benchmarkRes] = await Promise.all([
                api.getConsultation(sessionId),
                api.getConsultationBenchmark(sessionId)
            ]);

            setSessionData(sessionRes.data);
            setBenchmarkData(benchmarkRes.data.benchmark || []);
            setActiveTab('benchmark'); // Default to benchmark view if ID provided
        } catch (err) {
            console.error("Error fetching benchmark:", err);
        } finally {
            setLoading(false);
        }
    };

    // Old fetchDashboardData removed


    // --- Components ---

    const BenchmarkView = () => (
        <div className="space-y-6">
            <div className="bg-white p-6 rounded-xl shadow-sm border border-secondary-100">
                <h2 className="text-xl font-heading font-bold text-secondary-900 mb-4 flex items-center gap-2">
                    <Target className="w-5 h-5 text-primary-600" />
                    Golden Standard Benchmark
                </h2>
                <p className="text-secondary-600 mb-6">
                    Comparing your responses against expert protocols (CBT, REBT, SFBT).
                    <br />
                    <span className="text-sm text-secondary-500 italic">
                        * Only showing turns where significant improvement or specific protocols were identified.
                    </span>
                </p>

                <div className="space-y-8">
                    {benchmarkData.map((turn, index) => {
                        if (!turn) return null; // Skip irrelevant turns filtered by backend

                        // Determine if it's a "Playbook" match (has extra fields)
                        const isPlaybook = !!turn.why_correct;
                        // Determine match quality
                        const isGoodMatch = turn.distance < 0.4;

                        return (
                            <div key={index} className="relative pl-8 border-l-2 border-secondary-200">
                                {/* Timeline Dot */}
                                <div className={`absolute -left-[9px] top-0 w-4 h-4 rounded-full border-2 border-white 
                  ${isGoodMatch ? 'bg-green-500' : 'bg-amber-500'}`}></div>

                                {/* Turn Header */}
                                <div className="flex items-center gap-2 text-xs text-secondary-400 mb-2">
                                    <span className="font-mono">{turn.timestamp || `Turn ${index + 1}`}</span>
                                    {isPlaybook && (
                                        <span className="bg-primary-100 text-primary-700 px-2 py-0.5 rounded-full text-[10px] font-bold">
                                            PLAYBOOK MATCH
                                        </span>
                                    )}
                                </div>

                                {/* Dialogue Container */}
                                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">

                                    {/* Left: Actual Conversation */}
                                    <div className="space-y-3">
                                        <div className="bg-secondary-50 p-3 rounded-lg rounded-tl-none">
                                            <p className="text-xs font-bold text-secondary-500 mb-1">Patient</p>
                                            <p className="text-secondary-800">{turn.patient_input}</p>
                                        </div>
                                        <div className="bg-white border border-secondary-200 p-3 rounded-lg rounded-tr-none">
                                            <p className="text-xs font-bold text-secondary-500 mb-1">You Said</p>
                                            <p className="text-secondary-800">{turn.trainee_response}</p>
                                        </div>
                                    </div>

                                    {/* Right: Golden Standard / Recommendation */}
                                    <div className={`p-4 rounded-lg border ${isGoodMatch
                                        ? 'bg-green-50 border-green-100'
                                        : 'bg-amber-50 border-amber-100'
                                        }`}>
                                        <div className="flex justify-between items-start mb-2">
                                            <h4 className={`text-sm font-bold flex items-center gap-2 ${isGoodMatch ? 'text-green-800' : 'text-amber-800'
                                                }`}>
                                                {isGoodMatch ? <CheckCircle className="w-4 h-4" /> : <Zap className="w-4 h-4" />}
                                                {isGoodMatch ? "Excellent Match" : "Expert Alternative"}
                                            </h4>
                                            {turn.distance && (
                                                <span className="text-xs text-secondary-500" title="Lower is better">
                                                    Dist: {turn.distance.toFixed(3)}
                                                </span>
                                            )}
                                        </div>

                                        <div className="mb-3">
                                            <p className="text-xs font-bold text-secondary-500 mb-1">Expert Response</p>
                                            <p className="text-secondary-900 font-medium italic">
                                                "{turn.expert_response}"
                                            </p>
                                        </div>

                                        {/* Playbook Specifics */}
                                        {isPlaybook && (
                                            <div className="mt-3 pt-3 border-t border-secondary-200/50 space-y-2">
                                                {turn.therapist_intent && (
                                                    <p className="text-sm">
                                                        <span className="font-bold">Intent: </span>
                                                        {turn.therapist_intent}
                                                    </p>
                                                )}
                                                {turn.why_correct && (
                                                    <div className="flex gap-2 items-start">
                                                        <CheckCircle className="w-4 h-4 text-green-600 mt-0.5 shrink-0" />
                                                        <p className="text-sm text-secondary-700">
                                                            <span className="font-bold">Why:</span> {turn.why_correct}
                                                        </p>
                                                    </div>
                                                )}
                                                {turn.common_incorrect && (
                                                    <div className="flex gap-2 items-start">
                                                        <AlertCircle className="w-4 h-4 text-red-500 mt-0.5 shrink-0" />
                                                        <p className="text-sm text-secondary-700">
                                                            <span className="font-bold">Avoid:</span> {turn.common_incorrect}
                                                        </p>
                                                    </div>
                                                )}

                                            </div>
                                        )}

                                        {turn.expert_source && (
                                            <div className="mt-3 text-xs text-secondary-500 flex items-center gap-1">
                                                <BookOpen className="w-3 h-3" />
                                                Source: {turn.expert_source}
                                            </div>
                                        )}
                                    </div>

                                </div>
                            </div>
                        );
                    })}

                    {benchmarkData.filter(t => t).length === 0 && (
                        <div className="text-center py-12 bg-secondary-50 rounded-lg">
                            <CheckCircle className="w-12 h-12 text-green-500 mx-auto mb-3" />
                            <h3 className="text-lg font-bold text-secondary-900">Great Session!</h3>
                            <p className="text-secondary-600">
                                No major deviations from expert protocols were detected.
                                <br /> Your responses aligned well with standard guidelines.
                            </p>
                        </div>
                    )}
                </div>
            </div>
        </div>
    );

    if (loading) return <div className="p-8 text-center text-secondary-500">Loading dashboard...</div>;

    return (
        <DashboardLayout>
            <div className="max-w-7xl mx-auto px-4 py-8" data-tour="dashboard">
                {/* Header */}
                <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-8">
                    <div>
                        <h1 className="text-2xl font-heading font-bold text-secondary-900">
                            {id ? `Session Benchmark: #${id}` : "Clinician Dashboard"}
                        </h1>
                        <p className="text-secondary-600 mt-1">
                            {id
                                ? "Detailed comparison against expert protocols per turn."
                                : viewingDoctorId
                                    ? "Viewing session activity for a clinician under your supervision."
                                    : "Your session activity at a glance."}
                        </p>
                    </div>
                </div>

                {/* Date filter lives at the very top - it scopes Total Sessions/
                    Recent Activity below. The Yesterday/Today/Tomorrow
                    appointment row is intentionally independent of it (those
                    three days are always shown regardless of this filter). */}
                {!id && (
                    <div className="mb-6">
                        <PeriodPicker
                            options={SESSION_PERIOD_OPTIONS}
                            period={sessionPeriod}
                            onPeriodChange={setSessionPeriod}
                            customStart={sessionCustomStart}
                            onCustomStartChange={setSessionCustomStart}
                            customEnd={sessionCustomEnd}
                            onCustomEndChange={setSessionCustomEnd}
                        />
                    </div>
                )}

                {!id && (
                    <div className="mb-6">
                        <h3 className="text-sm font-bold text-secondary-500 uppercase tracking-wide mb-3 px-1 flex items-center gap-1.5">
                            Appointments
                            <InfoTip text="The big number is every appointment that existed for that day, including cancelled ones - true booking volume, not just what's still live. Missed and Cancelled below are components of that total (never the same appointment counted twice - a cancelled appointment can never also be missed, they're always two different bookings). Because cancelled appointments are counted here, this total won't match the Appointments list page, which never shows cancelled ones at all." />
                        </h3>
                        <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-6">
                            <Card className="p-5 flex flex-col h-full gap-3">
                                <div className="flex justify-between items-start">
                                    <span className="text-secondary-500 font-medium text-xs uppercase tracking-wide">Yesterday</span>
                                    <div className="p-1.5 bg-secondary-100 text-secondary-500 rounded-lg">
                                        <CalendarClock size={16} />
                                    </div>
                                </div>
                                <div>
                                    <span className="text-3xl font-heading font-bold text-secondary-900 block">{dayCounts.yesterday?.total ?? 0}</span>
                                    <span className="text-[10px] text-secondary-400">total appointments</span>
                                </div>
                                <div className="pt-2 border-t border-secondary-100">
                                    <p className="text-[10px] text-secondary-400 mb-1">Of which:</p>
                                    <div className="flex gap-4 text-xs text-secondary-500">
                                        <span className="flex items-center gap-1"><UserX size={12} className="text-amber-500" /> {dayCounts.yesterday?.missed ?? 0} missed</span>
                                        <span className="flex items-center gap-1"><XCircle size={12} className="text-red-500" /> {dayCounts.yesterday?.cancelled ?? 0} cancelled</span>
                                    </div>
                                </div>
                            </Card>
                            <Card className="p-5 flex flex-col h-full gap-3 ring-1 ring-primary-200 bg-primary-50/40">
                                <div className="flex justify-between items-start">
                                    <span className="text-primary-700 font-medium text-xs uppercase tracking-wide">Today</span>
                                    <div className="p-1.5 bg-primary-100 text-primary-600 rounded-lg">
                                        <CalendarClock size={16} />
                                    </div>
                                </div>
                                <div>
                                    <span className="text-3xl font-heading font-bold text-primary-900 block">{dayCounts.today?.total ?? 0}</span>
                                    <span className="text-[10px] text-primary-400">total appointments</span>
                                </div>
                                <div className="pt-2 border-t border-primary-100">
                                    <p className="text-[10px] text-secondary-400 mb-1">Of which:</p>
                                    <div className="flex gap-4 text-xs text-secondary-500">
                                        <span className="flex items-center gap-1"><UserX size={12} className="text-amber-500" /> {dayCounts.today?.missed ?? 0} missed</span>
                                        <span className="flex items-center gap-1"><XCircle size={12} className="text-red-500" /> {dayCounts.today?.cancelled ?? 0} cancelled</span>
                                    </div>
                                </div>
                            </Card>
                            <Card className="p-5 flex flex-col h-full gap-3">
                                <div className="flex justify-between items-start">
                                    <span className="text-secondary-500 font-medium text-xs uppercase tracking-wide">Tomorrow</span>
                                    <div className="p-1.5 bg-secondary-100 text-secondary-500 rounded-lg">
                                        <CalendarClock size={16} />
                                    </div>
                                </div>
                                <div>
                                    <span className="text-3xl font-heading font-bold text-secondary-900 block">{dayCounts.tomorrow?.total ?? 0}</span>
                                    <span className="text-[10px] text-secondary-400">active appointments</span>
                                </div>
                            </Card>
                        </div>
                    </div>
                )}

                {!id && (
                    <div className="mb-6">
                        <DueFollowUpsCard doctorId={viewingDoctorId} />
                    </div>
                )}

                {/* Main Content */}
                {id ? (
                    <BenchmarkView />
                ) : (
                    <div className="space-y-6">
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 md:gap-6">
                            <Card className="p-5 flex flex-col justify-between h-32 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-default group">
                                <div className="flex justify-between items-start">
                                    <span className="text-secondary-500 font-medium text-sm uppercase tracking-wide">Total Sessions</span>
                                    <div className="p-2 bg-green-100 text-green-600 rounded-lg group-hover:rotate-12 transition-transform">
                                        <Calendar size={20} />
                                    </div>
                                </div>
                                <span className="text-4xl font-heading font-bold text-secondary-900 block">{sessionCount}</span>
                            </Card>
                            <Card className="p-5 flex flex-col justify-between h-32 hover:scale-[1.02] active:scale-[0.98] transition-all cursor-default group">
                                <div className="flex justify-between items-start">
                                    <span className="text-secondary-500 font-medium text-sm uppercase tracking-wide">Available AI Credits</span>
                                    <div className="p-2 bg-amber-100 text-amber-600 rounded-lg group-hover:rotate-12 transition-transform">
                                        <CreditCard size={20} />
                                    </div>
                                </div>
                                <div className="flex items-baseline gap-2">
                                    <span className="text-4xl font-heading font-bold text-secondary-900 block">{personalCredits}</span>
                                    <span className="text-xs text-secondary-400">credits</span>
                                </div>
                            </Card>
                        </div>
                        <RecentActivityCard sessions={recentSessions} onRetried={() => fetchSessionStats(sessionRange?.start || null, sessionRange?.end || null)} />
                    </div>
                )}
            </div>
        </DashboardLayout>
    );
};

export default TherapistDashboard;
